import { Role } from '../enums/role.enum';

/**
 * Capability vocabulary — authorization is gated by these, never by role identity
 * (ADR-015). Seeded from Backend Business-Logic **Diagram 4** (the permission matrix).
 * This is a representative, extensible set covering the headline module actions; add
 * capabilities here (and to `ROLE_PERMISSIONS`) as each module is ported. Read-only
 * "view" capabilities are listed where a feature needs to gate visibility distinctly.
 *
 * ⚠️ Cells marked "pending Mitch" in the source (e.g. TA/TM/TW NBD-rule *view*, NAM
 * NBD-invoice approval, NAM WO cancel, TM revoke Sub Proxy) are encoded per the doc's
 * current decision and flagged in comments — finalizing them is a data edit here only.
 */
export enum Permission {
  // Territory management
  TerritoryCreate = 'territory:create',
  TerritoryEdit = 'territory:edit',
  TerritoryDelete = 'territory:delete',
  TerritorySwitch = 'territory:switch',
  TerritoryFinanceSettings = 'territory:finance-settings',
  /** Corporate cross-territory "Financial Overview" (rates-configured completeness across all
   *  territories). Distinct from TerritoryFinanceSettings (editing one territory's own rates, which
   *  territory admins also hold). Corporate-only today; change the grant — not the UI — to widen it. */
  TerritoryFinanceOverview = 'territory:finance-overview',
  /** Client -> NBD/corporate payment rail (migration 180, vantage-infra): create a corporate
   *  billing entity and start its Stripe Connect onboarding. Matches the backend's exact
   *  `require_role(SUPER_ADMIN, CORPORATE_WRITER)` guard on `corporate_entities_api` — see
   *  ROLE_PERMISSIONS below. */
  CorporateEntityWrite = 'corporate-entity:write',
  /**
   * #ACH-1: start (or resume) a territory's Stripe Connect payout onboarding
   * (`POST /territories/{id}/stripe-connect`). Creating a connected account is the authority to
   * route the territory's money, so the backend's `_connect_writer` (`territories_api/routes.py`,
   * fixed in vantage-backend#710) takes the manager-plus tier — SUPER_ADMIN, CORPORATE_WRITER,
   * TERRITORY_ADMIN, TERRITORY_MANAGER — not the ordinary writer set.
   */
  TerritoryConnectWrite = 'territory:connect-write',
  // User management
  /**
   * Access the Users admin surface (Settings → Users: list, create, edit). **SUPER_ADMIN-only for
   * now** — granted solely via the `ALL` bundle and listed in no other role (owner directive
   * 2026-07-21). This is a deliberate "for now" narrowing of the documented target model (where
   * Territory Admins manage territory users and Corporate adds territory-admin users); when RBAC is
   * finalized, widen it by adding this permission to the relevant roles — **no UI change needed**.
   * See PORT_DEVIATIONS.
   */
  UsersManage = 'user:manage',
  UserCreateCorp = 'user:create-corp',
  UserCreateTerritoryAdmin = 'user:create-territory-admin',
  UserCreateTerritoryStaff = 'user:create-territory-staff',
  UserCreateSubAdmin = 'user:create-sub-admin',
  UserCreateSubProfile = 'user:create-sub-profile',
  UserCreateSubStaff = 'user:create-sub-staff',
  UserCreateClient = 'user:create-client',
  /**
   * Start a staff member's password reset — `POST /users/{id}/reset-password`
   * ([Decision CP2](../../../../docs/rbac-matrix.md), 2026-09-24).
   *
   * Mirrors the route's own `_writer_or_above` guard exactly: SUPER_ADMIN, CORPORATE_WRITER,
   * TERRITORY_ADMIN. Deliberately NOT `UsersManage`, which is the roster's edit gate and is held
   * more widely — a reader who may rename a colleague is not thereby a reader who may take over
   * their sign-in.
   *
   * ⚠️ Nobody sets a password here, including the admin who presses it: Cognito emails the user a
   * code and they choose their own. That is why this is a reset TRIGGER and not a credential
   * surface, and the confirmation copy says so.
   */
  UserPasswordReset = 'user:password-reset',
  /**
   * #ACH-2 self-service: a subcontractor starting (or resuming) THEIR OWN Stripe Connect payout
   * onboarding from `/sub/v1/payouts/stripe-connect`. SUB_ADMIN-only, mirroring the backend's
   * `_connect_writer` guard on that route — the same money-moving posture the internal
   * `Permission.SubcontractorConnectWrite` takes for the admin-initiated path (SUPER_ADMIN /
   * TERRITORY_ADMIN only, TERRITORY_MANAGER deliberately excluded). Distinct permission because
   * this gates a different portal's route on a different role set.
   */
  SubPayoutsConnectWrite = 'sub-payouts:connect-write',
  GrantSubProxy = 'user:grant-sub-proxy',
  // TERRITORY_MANAGER holds this and SUPER_ADMIN/TERRITORY_ADMIN do too — RULED as Decision CP3
  // (2026-09-24), which closes the "pending Mitch" question this line carried. Grant stays the
  // narrower pair; only revoke reaches TM.
  RevokeSubProxy = 'user:revoke-sub-proxy',
  ForceLogout = 'user:force-logout',
  // Accounts
  AccountCreateNational = 'account:create-national',
  AccountEditNational = 'account:edit-national',
  NbdDivisionCreate = 'account:nbd-division-create',
  NbdDivisionEdit = 'account:nbd-division-edit',
  AccountCreateLocal = 'account:create-local',
  AccountEditLocal = 'account:edit-local',
  AccountSoftDelete = 'account:soft-delete',
  /**
   * Add, retype or delete a governance **rule** on an account or one of its locations.
   *
   * Mirrors the backend's `_rules_writer` exactly — `require_role("SUPER_ADMIN",
   * "CORPORATE_WRITER", "TERRITORY_ADMIN", "TERRITORY_MANAGER")`, declared identically in
   * `accounts_api/routes.py:40` and `locations_api/routes.py:37`, covering all six write routes
   * (`POST|PATCH|DELETE` on `/accounts/{id}/rules` and `/locations/{id}/rules`).
   *
   * ⚠️ **Deliberately NOT in `TERRITORY_MODULE`, and that is the whole reason it is its own
   * capability.** `TERRITORY_WRITER` spreads that bundle, and `_rules_writer` is the CRM write set
   * that omits TERRITORY_WRITER — so gating these controls on `AccountEditLocal` (which the bundle
   * grants) hands a territory writer an Add Rule button the API answers 403. Same shape and same
   * reasoning as `SubcontractorInviteAdmin`, whose route omits the role for the same reason.
   * `rbac-matrix.md`'s "locations rules-writer" footnote is the documented half.
   */
  AccountRulesManage = 'account:rules-manage',
  /**
   * Set or remove an account's **"Managed by"** assignment (Decision CF2, vantage-docs#242) —
   * the `user_account_access` grant with `role = 'ACCOUNT_MANAGER'`, written via
   * `POST /users/{id}/account-access` / `DELETE /users/{id}/account-access/{account_id}`.
   *
   * Mirrors the backend's exact guard on both routes: `_corp_writer =
   * require_role("SUPER_ADMIN", "CORPORATE_WRITER")` (`users_api/routes.py`). Corporate-only by
   * ruling: a territory user sees who manages an account and may not change it — the control
   * renders DISABLED with this refusal as its reason (owner's one-view-per-surface rule,
   * 2026-08-25), never hidden. The assignment confers a duty, not a permission tier: the holder
   * is the account's NTE-exception reviewer (Decision CF1), and eligibility to HOLD it (an
   * active SUPER_ADMIN or CORPORATE_WRITER) is a data filter on the picker, not a gate here.
   */
  AccountManagedByAssign = 'account:managed-by-assign',
  /**
   * Resolve an outstanding service night by administrative authority (Decision BA,
   * vantage-backend#539) — `POST /crm/v1/work-orders/days/{day_id}/override`.
   *
   * Its own capability rather than a reuse: the backend gates it on `_overrider =
   * require_role("TERRITORY_ADMIN", "SUPER_ADMIN")`, which is narrower than every other write on
   * the nights card. BA is explicit that other territory staff "may complete a job once every gate
   * is clear but may not override" — so the authority to say a night is resolved WITHOUT anyone
   * having been on site is deliberately not the authority to run the job.
   */
  WorkOrderDayOverride = 'work-order-day:override',
  // Subcontractors
  /**
   * Create / edit / delete a subcontractor, and manage its account assignments and service
   * categories. ONE capability, because the backend gates all seven write routes with the same
   * `_writer` set — SUPER_ADMIN, TERRITORY_ADMIN, TERRITORY_MANAGER, TERRITORY_WRITER
   * (`subcontractors_api/routes.py`): `POST /subcontractors/`, `PATCH|DELETE /{sub_id}`,
   * `POST|PATCH|DELETE /{sub_id}/accounts[/{assignment_id}]`, `PUT /{sub_id}/service-categories`.
   * Splitting it per verb would produce identical grants three times over.
   *
   * Reads are deliberately NOT gated by this: every subcontractor GET is
   * `require_portal("CORPORATE", "TERRITORY")`, so corporate staff read the roster and the
   * detail page. They simply hold no write capability here — the `_writer` set names no
   * corporate role, and a national account manager signs in as CORPORATE_WRITER
   * (vantage-docs#80), which it also omits.
   */
  SubcontractorWrite = 'subcontractor:write',
  /**
   * Send a subcontractor's portal-admin invite (`POST /subcontractors/{sub_id}/invite-admin`).
   * A NARROWER set than {@link SubcontractorWrite} — SUPER_ADMIN, TERRITORY_ADMIN,
   * TERRITORY_MANAGER. The route does not name TERRITORY_WRITER, so this is its own capability
   * rather than a reuse of the write gate.
   */
  SubcontractorInviteAdmin = 'subcontractor:invite-admin',
  /**
   * Record — or withdraw — a subcontractor's workers'-comp **exemption**
   * (the `comp_exempt` half of `PATCH /subcontractors/{id}`, vantage-backend#860).
   *
   * The same narrow set as {@link SubcontractorInviteAdmin}: SUPER_ADMIN, TERRITORY_ADMIN,
   * TERRITORY_MANAGER — `_EXEMPTION_RECORDERS` in `subcontractors_api/service.py:273`, which the
   * service checks separately from the route's own `_writer` guard. So TERRITORY_WRITER may edit a
   * subcontractor and may **not** record an exemption, and reusing {@link SubcontractorWrite} here
   * would offer a control the API refuses (RBP #23).
   *
   * ⚠️ **An exemption is a legitimate compliance state, not an override** (ruling A4's sole-operator
   * rider). It satisfies the workers'-comp requirement rather than waiving it, which is why it is
   * recorded against the record with a reason the database insists on, and why the same capability
   * covers withdrawing it: both directions are the same audited fact changing.
   */
  SubcontractorCompExemption = 'subcontractor:comp-exemption',
  /** Reassign a subcontractor's home territory on edit. SUPER_ADMIN-only — granted solely via
   *  the `ALL` bundle, listed in no other role, mirroring the backend's super-admin gate on the
   *  `territory_id` field of `PATCH /subcontractors/{id}`. */
  SubcontractorReassignTerritory = 'subcontractor:reassign-territory',
  /** Correct a service location's SERVICING territory on edit. SUPER_ADMIN-only — granted solely
   *  via the `ALL` bundle, listed in no other role (owner ruling, ryanlacy1 2026-09-09, on
   *  vantage-backend#757/#759), mirroring the backend's super-admin narrowing on the
   *  `territory_id` field of `PATCH /locations/{id}`. The move re-stamps the site's operational
   *  rows server-side (composite-FK cascade); a subcontractor still assigned from the old market
   *  blocks it with a 409. */
  LocationReassignTerritory = 'location:reassign-territory',
  /**
   * #ACH-2: start (or resume) a subcontractor's Stripe Connect payout onboarding
   * (`POST /subcontractors/{sub_id}/stripe-connect`). A NARROWER set than
   * {@link SubcontractorWrite} — SUPER_ADMIN, TERRITORY_ADMIN only (`_connect_writer` in
   * `subcontractors_api/routes.py`), per owner decision dropping TERRITORY_MANAGER since this
   * one moves real money rather than editing a record.
   */
  SubcontractorConnectWrite = 'subcontractor:connect-write',
  // NBD rules
  NbdRulesView = 'nbd:view', // ⚠️ TA/TM/TW read pending Mitch
  NbdMarginOverride = 'nbd:margin-override',
  // Quote requests (NBD)
  QuoteRequestCreate = 'quote-request:create',
  QuoteRequestReceive = 'quote-request:receive',
  QuoteApprove = 'quote-request:approve',
  QuoteReject = 'quote-request:reject',
  // Quotes
  QuoteCreate = 'quote:create',
  QuoteSendLocal = 'quote:send-local',
  QuoteSendNbd = 'quote:send-nbd',
  QuoteAccept = 'quote:accept',
  QuoteDecline = 'quote:decline',
  QuoteConvertWo = 'quote:convert-wo',
  QuoteViewCost = 'quote:view-cost',
  // Bid requests
  BidRequestCreate = 'bid:request-create',
  BidView = 'bid:view',
  BidSubmit = 'bid:submit',
  BidAward = 'bid:award',
  /**
   * Release the SUBCONTRACTOR half of the two-sided acceptance gate when a sub goes silent
   * (vantage-backend#219, Decision J3). Mirrors `_acceptance_overrider` in `bids_api/routes.py` —
   * `require_role("SUPER_ADMIN", "CORPORATE_WRITER")` — as a capability rather than a role test
   * (ADR-015), so the two grants below are the whole set and adding a role does not silently widen it.
   * `app_override_bid_acceptance` enforces the same set in the database, so this gate hides a
   * control nobody else could use rather than being the thing that stops them.
   */
  BidAcceptanceOverride = 'bid:acceptance-override',
  // Work orders
  WoCreate = 'wo:create',
  WoAssignSub = 'wo:assign-sub',
  WoUpdateStatus = 'wo:update-status',
  WoComplete = 'wo:complete',
  WoCancel = 'wo:cancel', // ⚠️ NAM NBD cancel pending Mitch
  WoChecklistComplete = 'wo:checklist-complete',
  /** Record the operator's verification stamp on one-time work. Mirrors the backend's
   * SUPER_ADMIN + territory-admin/manager/writer guard; corporate and portal roles are excluded. */
  WoOperatorSignOff = 'wo:operator-sign-off',
  WoView = 'wo:view',
  // Service requests
  SrFile = 'sr:file',
  SrAssign = 'sr:assign',
  SrConvertQuoteRequest = 'sr:convert-quote-request',
  SrResolve = 'sr:resolve',
  SrConvertWo = 'sr:convert-wo',
  /**
   * The three atomic triage exits — corrective visit, corrective + standing scope, schedule change —
   * which all POST to `/service-requests/{id}/triage`.
   *
   * Its own capability because that route's guard is NOT the one the rest of the module uses:
   * `_territory_writer` (SUPER_ADMIN + TA/TM/TW), deliberately excluding CORPORATE_WRITER because the
   * rows it writes — a territory's own checklist items and recurring schedules — have no corporate
   * policy arm (`service_requests_api/routes.py`, citing the vantage-backend#210 ruling).
   */
  SrTriage = 'sr:triage',
  SrView = 'sr:view',
  // Inspections
  InspectionCreate = 'inspection:create',
  InspectionScore = 'inspection:score',
  /**
   * Raise a **corrective work order** from an inspection issue
   * (`POST /crm/v1/work-orders/from-issue/{issue_id}`).
   *
   * Deliberately NOT {@link Permission.InspectionScore}, and the difference is the whole point of
   * the entry existing. Scoring is `inspections_api`'s `_writer` — five roles. This route is
   * `work_orders_api`'s `_corrective_creator`, which is **SUPER_ADMIN and TERRITORY_ADMIN only**:
   * raising a corrective order commits crew time and money, so it sits a tier above recording the
   * finding that prompted it.
   *
   * Gating the button on `InspectionScore` would have looked correct and refused nobody, while a
   * TERRITORY_WRITER kept clicking it into a 403 — a gate that disagrees with the server is worse
   * than no gate, because it reads as having been checked.
   */
  InspectionCorrectiveWoCreate = 'inspection:corrective-wo-create',
  /**
   * Log a sales opportunity spotted on a walk — the CA2 ten-second capture
   * (`POST /crm/v1/inspections/{inspection_id}/opportunities`).
   *
   * TERRITORY-ONLY, and NOT {@link Permission.InspectionScore}, because the capture creates a
   * **deal** rather than an inspection row. The route guard is `inspections_api`'s five-role
   * `_writer`, but the service refuses a corporate writer before the INSERT: `deals_modify` RLS has
   * no corporate arm, so CORPORATE_WRITER passes the guard and fails the write. Reading the route
   * guard alone gets this one wrong — the narrowing is in the service, and it is load-bearing.
   *
   * Lives in `TERRITORY_MODULE`, which resolves it to the territory writer set exactly.
   */
  InspectionOpportunityCapture = 'inspection:opportunity-capture',
  /**
   * Release a held subcontractor debrief
   * (`POST /crm/v1/inspections/{inspection_id}/debrief/release`, BV3).
   *
   * TERRITORY-ONLY, and — as with {@link Permission.InspectionOpportunityCapture} — the route guard
   * does NOT say so. `inspections_api` admits the whole five-role `_writer`; the SERVICE then checks
   * `claims.portal != "TERRITORY"` and refuses 403 `DEBRIEF_RELEASE_TERRITORY_ONLY`, quoting BV3:
   * *"the outbound subcontractor message waits for a territory user to release it"*. A corporate
   * writer passes the decorator and is refused by the handler.
   *
   * Lives in `TERRITORY_MODULE`, which resolves it to the territory writer set exactly.
   */
  InspectionDebriefRelease = 'inspection:debrief-release',
  /**
   * Author the reusable **crew checklist** library — create / rename / (de)activate / delete a
   * checklist template and replace its items (`/oip/settings/checklist-templates`).
   *
   * Distinct from every `Inspection*` capability above: those gate what an INSPECTOR scores 0-5 into
   * the health score; this gates what a CREW performs on a visit. Same word, different concept — see
   * the crosswalk in `vantage-infra/docs/nomenclature.md`.
   *
   * Mirrors the route's `_writer` exactly (SUPER_ADMIN, CORPORATE_WRITER, TA/TM/TW); RLS narrows a
   * territory writer to its own territory, which a capability cannot express, so a cross-territory
   * write is refused by the row policy rather than by this gate.
   */
  ChecklistTemplateManage = 'checklist-template:manage',

  // Scope of Record (Decision A). Two tiers, because the backend has two and they are not the same
  // act: proposing an amendment is open to the field, committing one BINDS us.
  /** Author the record itself — create a cell, publish a version, add/edit/remove items. */
  SowEdit = 'sow:edit',
  /** Raise an amendment from the field. Deliberately wider than SowEdit. */
  SowAmendmentPropose = 'sow:amendment-propose',
  /** Commit an amendment. Manager-or-above; the backend refuses below that with a stated reason. */
  SowAmendmentCommit = 'sow:amendment-commit',
  InspectionView = 'inspection:view',
  InspectionViewOwn = 'inspection:view-own',
  // AR — client invoices
  InvoiceCreate = 'invoice:create',
  InvoiceApproveNbd = 'invoice:approve-nbd', // ⚠️ pending Mitch
  InvoiceApproveLocal = 'invoice:approve-local',
  InvoiceMarkPaid = 'invoice:mark-paid',
  InvoiceVoid = 'invoice:void',
  InvoiceView = 'invoice:view',
  // AP — vendor bills
  VendorBillCreate = 'vendor-bill:create',
  VendorBillApprove = 'vendor-bill:approve',
  VendorBillDispute = 'vendor-bill:dispute',
  VendorBillView = 'vendor-bill:view',
  /**
   * #ACH-2: start a card/ACH Direct-charge payout to the bill's subcontractor
   * (`POST /vendor-bills/{bill_id}/payment-intent`). A NARROWER set than
   * {@link VendorBillApprove} — SUPER_ADMIN, TERRITORY_ADMIN only (`_payer` in
   * `vendor_bills_api/routes.py`), per owner decision dropping TERRITORY_MANAGER since this one
   * moves real money rather than flipping a status flag.
   */
  VendorBillPayoutCreate = 'vendor-bill:payout-create',
  // National approvals queue
  ApprovalsView = 'approvals:view',
  ApprovalsApprove = 'approvals:approve',
  ApprovalsReject = 'approvals:reject',
  // Documents
  DocUpload = 'doc:upload',
  DocMarkClientVisible = 'doc:mark-client-visible',
  DocDownload = 'doc:download',
  // Messages
  MsgSendSub = 'msg:send-sub',
  MsgSendLocalClient = 'msg:send-local-client',
  MsgSendNbdClient = 'msg:send-nbd-client',
  MsgReply = 'msg:reply',
  // Settings & config
  SettingsAccess = 'settings:access',
  PlatformConfig = 'settings:platform-config',
  UdcManage = 'settings:udc-manage',
  // `DbMigrate` / `SystemHealth` were here until 2026-08-29. They gated the replaced system's
  // admin tooling — schema atlas, ERD/migrations, health check, backup/restore — which parity
  // `settings.md` D9 (Rob, 2026-08-24) ruled OUT OF PILOT SCOPE: every one of them is replaced by
  // platform infrastructure (AWS Backup, CloudWatch alarms, the single migration tree and its
  // combined-schema check) rather than reimplemented. Neither had a `hasPermission` call, a route in
  // vantage-web, or a route in vantage-backend — a capability granted for tooling that will never be
  // built. Removed rather than waived: a waiver says "not yet", and D9 says never.
  // `RoyaltiesManage` was here until 2026-08-29. Royalties were backlogged by Decision D11
  // (2026-08-15); parity `settings.md` D9 (2026-08-24) then kept the orphaned client in place, and
  // that ruling was superseded on 2026-08-29 by its own decider, who called for the module to be
  // deleted. The web slice, the backend `finance_api/royalties` routes and this capability went
  // together. The TABLES stay, per D11 — migrations are forward-only.
  // Tasks
  TaskCreate = 'task:create',
  TaskComplete = 'task:complete',
  // Recurring schedules
  RsCreateLocal = 'rs:create-local',
  RsCreateNational = 'rs:create-national',
  RsView = 'rs:view',
  /**
   * AI assistant (Phase A): open the chat widget and send turns. Granted to every
   * INTERNAL role — the assistant confers nothing: each tool call runs through the REST
   * APIs with the caller's own token, so what the assistant can read/do is exactly the
   * caller's `require_role` + RLS surface. Deliberately NOT granted to sub/client
   * portal roles (the backend mount is CRM-groups only in Phase A).
   */
  AssistantUse = 'assistant:use',
}

const ALL = Object.values(Permission);

// Reusable bundle: the "full module access" granted to TA/TM/TW (Diagram 4 territory rows).
const TERRITORY_MODULE: Permission[] = [
  Permission.InspectionOpportunityCapture,
  Permission.InspectionDebriefRelease,
  Permission.TerritorySwitch,
  Permission.AccountCreateLocal, Permission.AccountEditLocal,
  Permission.NbdRulesView, // ⚠️ pending Mitch
  // Both halves of the quote-request door: territories RAISE them (`POST /crm/v1/quotes/requests`,
  // `_writer` = SUPER_ADMIN + TA/TM/TW) as well as receive them. Only Receive was granted.
  Permission.QuoteRequestReceive, Permission.QuoteRequestCreate,
  Permission.QuoteCreate, Permission.QuoteSendLocal, Permission.QuoteConvertWo,
  Permission.BidRequestCreate, Permission.BidAward,
  Permission.WoCreate, Permission.WoAssignSub, Permission.WoUpdateStatus,
  Permission.WoComplete, Permission.WoCancel, Permission.WoChecklistComplete,
  Permission.WoOperatorSignOff, Permission.WoView,
  // SrConvertQuoteRequest + SrTriage added 2026-08-14: the backend names TERRITORY_ADMIN/MANAGER/WRITER
  // on BOTH routes — `_writer` on `POST /{id}/reclassify-to-quote-request`, `_territory_writer` on
  // `POST /{id}/triage` — and withholding them here would have hidden five of the seven triage exits
  // from the only people who use the intake queue (RBP #23, the UI-stricter-than-backend direction).
  Permission.SrFile, Permission.SrAssign, Permission.SrResolve, Permission.SrConvertWo, Permission.SrView,
  Permission.SrConvertQuoteRequest, Permission.SrTriage,
  Permission.InspectionCreate, Permission.InspectionScore, Permission.InspectionView,
  // The crew-checklist library. `POST|PATCH|DELETE /crm/v1/inspections/checklist-templates` names
  // TERRITORY_ADMIN/MANAGER/WRITER alongside the corporate writer, so the bundle is the right home.
  Permission.ChecklistTemplateManage,
  Permission.InvoiceCreate, Permission.InvoiceView,
  Permission.VendorBillView,
  // The roster's write surface. `_writer` on every subcontractor mutation is SUPER_ADMIN +
  // TA/TM/TW — exactly this bundle's audience — so it belongs here rather than on each role.
  // `SubcontractorInviteAdmin` deliberately does NOT: its route omits TERRITORY_WRITER, and TW
  // spreads this bundle.
  Permission.SubcontractorWrite,
  Permission.TaskCreate, Permission.TaskComplete,
  Permission.RsCreateLocal, Permission.RsView,
  Permission.DocUpload, Permission.DocMarkClientVisible, Permission.DocDownload,
  Permission.MsgSendSub, Permission.MsgSendLocalClient,
];

// Sub portal "field" capabilities (Diagram 4 sub rows).
const SUB_WORKER: Permission[] = [
  Permission.BidView, Permission.BidSubmit,
  Permission.WoUpdateStatus, Permission.WoComplete, Permission.WoChecklistComplete, Permission.WoView,
  Permission.VendorBillCreate, Permission.VendorBillView,
  Permission.InspectionViewOwn, Permission.DocUpload, Permission.DocDownload, Permission.MsgReply,
];

/**
 * Role → granted capabilities (Diagram 4). `SUPER_ADMIN` gets everything. Treat ✅ and 🔒
 * (scoped) as "granted" here — row-level scoping (assigned/own/territory) is enforced by the
 * backend RLS + the `X-Territory-ID`/account-scope context (TODO, see state-management.md §4),
 * not by this UI capability gate.
 */
export const ROLE_PERMISSIONS: Record<Role, readonly Permission[]> = {
  [Role.SuperAdmin]: ALL,

  [Role.CorporateWriter]: [
    // The acceptance override is corporate-only: `_acceptance_overrider` is
    // `require_role("SUPER_ADMIN", "CORPORATE_WRITER")`, and SuperAdmin holds ALL above. It must NOT
    // go in TERRITORY_MODULE — a territory admin releasing a sub's half is precisely the thing the
    // backend and `app_override_bid_acceptance` both refuse (RBP #23, UI-wider-than-backend).
    Permission.BidAcceptanceOverride,
    Permission.SowEdit, Permission.SowAmendmentPropose, Permission.SowAmendmentCommit,
    // TerritoryDelete: `DELETE /territories/{id}` is gated `_corp_writer` = SUPER_ADMIN +
    // CORPORATE_WRITER (territories_api/routes.py). We held it SUPER_ADMIN-only. No longer latent:
    // the territory record's Delete button is gated on this capability (territory-detail.ts
    // `canDelete`), which is what closed the role-equality gate that hid it from this role.
    Permission.TerritoryCreate, Permission.TerritoryEdit, Permission.TerritoryDelete,
    Permission.TerritorySwitch,
    Permission.TerritoryFinanceOverview,
    Permission.CorporateEntityWrite,
    Permission.TerritoryConnectWrite,
    Permission.UserCreateTerritoryAdmin, Permission.UserCreateClient, Permission.UserPasswordReset,
    Permission.AccountCreateNational, Permission.AccountEditNational,
    // Managed-by assignment (CF2): the account-access routes' `_corp_writer` guard names this
    // role explicitly, and SuperAdmin holds ALL above.
    Permission.AccountManagedByAssign,
    // Rules: `_rules_writer` names CORPORATE_WRITER on both sub-resources (accounts + locations).
    Permission.AccountRulesManage,
    // AccountSoftDelete: both layers admit this role — route `_deleter` =
    // SUPER_ADMIN/TERRITORY_ADMIN/CORPORATE_WRITER (accounts_api/routes.py) and RLS `accounts_delete`
    // = the same three. We had SUPER_ADMIN + TERRITORY_ADMIN only.
    Permission.AccountSoftDelete,
    Permission.NbdDivisionCreate, Permission.NbdDivisionEdit,
    Permission.NbdRulesView, Permission.NbdMarginOverride,
    Permission.QuoteViewCost,
    // R5 (ADR-015 pass on the quote lifecycle): the backend admits CORPORATE_WRITER on these —
    // `_inviter` (send) and `_creator` (convert) are SUPER_ADMIN + CORPORATE_WRITER + TA/TM/TW,
    // and `_releaser` (the NBD release/approve) is SUPER_ADMIN + CORPORATE_WRITER
    // (quotes_api/routes.py). We held them territory-side or SuperAdmin-only, which is the
    // UI-stricter-than-backend direction (RBP #23) — it would disable Send/Release/Convert for
    // the corporate desk that demonstrably uses them. QuoteCreate stays withheld on purpose:
    // `create_quote` is `_writer`, which does NOT name CORPORATE_WRITER (role-guard-parity).
    Permission.QuoteSendLocal, Permission.QuoteSendNbd, Permission.QuoteConvertWo,
    Permission.InvoiceCreate, Permission.InvoiceApproveNbd, Permission.InvoiceMarkPaid,
    Permission.InvoiceVoid, Permission.InvoiceView,
    Permission.InspectionCreate, Permission.InspectionScore, Permission.InspectionView,
    // The crew-checklist library: the route's `_writer` names CORPORATE_WRITER explicitly, so
    // withholding it here would hide the authoring surface from a role the backend accepts (RBP #23).
    Permission.ChecklistTemplateManage,
    // Reviews approvals: the backend admits this role at both gates — route
    // `require_role("SUPER_ADMIN", "CORPORATE_WRITER", "NAM")` and RLS
    // `na_update: app_is_corporate_writer() OR app_is_nam()` (vantage-infra 003_rls_policies.sql:549).
    // We previously granted View only, which is a UI-stricter-than-backend divergence: it would have
    // hidden Approve/Reject from a role that demonstrably uses them — the nightly full-stack
    // approval-workbench signs in as CORPORATE_WRITER and reviews against a real backend.
    Permission.ApprovalsView, Permission.ApprovalsApprove, Permission.ApprovalsReject,
    // RsCreateNational dropped 2026-08-14 — RULED vestigial by @Bereket-Desbele on
    // vantage-backend#240, from the code rather than from intent: no route and no row policy admits
    // a corporate role to create a recurring schedule, and the national flow is
    // "territory creates, corporate approves" — the schedule detail already tells the user so
    // ("Pending corporate approval — this national-account schedule is held until reviewed in NBD
    // Approvals"). A create arm would be a second, unreviewed path that BYPASSES that hold.
    Permission.DocUpload, Permission.DocDownload,
    // CRM messaging: both message APIs gate writes (send / mark-read / escalate) with the same
    // `_writer` set — SUPER_ADMIN, CORPORATE_WRITER, TERRITORY_ADMIN/MANAGER/WRITER
    // (messages_api/routes.py + client_messages_api/routes.py). We previously granted CW neither
    // Msg capability, which would hide the unified inbox's compose/reply from a role the backend
    // accepts — the silent direction of UI ⇄ backend drift (RBP #23).
    Permission.MsgSendSub, Permission.MsgSendLocalClient,
    // Service requests: this role held NOTHING here, not even read. The backend admits it at three
    // gates — `require_portal("CORPORATE","TERRITORY")` on every GET, and `_writer` (which names
    // CORPORATE_WRITER explicitly) on `PATCH /{id}` and `POST /{id}/reclassify-to-quote-request`,
    // backed by the corporate arm migration 047 added to the `sr_update` policy. Withholding SrView
    // alone would have blanked the intake queue for corporate.
    //
    // Deliberately NOT here: `SrFile` and `SrTriage` (both `_territory_writer`, no corporate arm —
    // the routes' own comment says neither ruling asked for one), and `SrConvertWo`, whose exit
    // navigates to a work-order create form this role is not granted.
    //
    // Caveat worth knowing: RLS narrows the corporate arm to `app_account_is_national(account_id)`,
    // which a capability cannot express. A corporate user acting on a LOCAL account's request is
    // refused by the row policy, not by this list.
    Permission.SrView, Permission.SrResolve, Permission.SrConvertQuoteRequest,
    Permission.SettingsAccess, Permission.PlatformConfig, Permission.UdcManage,
    Permission.AssistantUse,
  ],

  [Role.TerritoryAdmin]: [
    Permission.SowEdit, Permission.SowAmendmentPropose, Permission.SowAmendmentCommit,
    ...TERRITORY_MODULE,
    Permission.SettingsAccess, Permission.TerritoryFinanceSettings, Permission.AccountSoftDelete,
    Permission.AccountRulesManage,
    Permission.WorkOrderDayOverride, Permission.InspectionCorrectiveWoCreate,
    Permission.UserCreateTerritoryStaff, Permission.UserCreateSubAdmin, Permission.UserCreateSubProfile,
    Permission.UserCreateSubStaff, Permission.UserCreateClient, Permission.UserPasswordReset,
    Permission.GrantSubProxy, Permission.RevokeSubProxy,
    Permission.SubcontractorInviteAdmin, Permission.SubcontractorConnectWrite,
    Permission.SubcontractorCompExemption,
    Permission.TerritoryConnectWrite,
    Permission.InvoiceApproveLocal, Permission.InvoiceMarkPaid, Permission.InvoiceVoid,
    Permission.VendorBillApprove, Permission.VendorBillDispute, Permission.VendorBillPayoutCreate,
    Permission.AssistantUse,
  ],

  [Role.TerritoryManager]: [
    Permission.SowEdit, Permission.SowAmendmentPropose, Permission.SowAmendmentCommit,
    ...TERRITORY_MODULE,
    Permission.UserCreateSubAdmin, Permission.UserCreateSubProfile, Permission.UserCreateSubStaff,
    Permission.UserCreateClient, Permission.RevokeSubProxy, // ruled: Decision CP3
    Permission.AccountRulesManage,
    Permission.SubcontractorInviteAdmin, Permission.SubcontractorCompExemption,
    Permission.TerritoryConnectWrite,
    Permission.InvoiceApproveLocal, Permission.InvoiceMarkPaid,
    Permission.VendorBillApprove, Permission.VendorBillDispute,
    Permission.AssistantUse,
  ],

  [Role.TerritoryWriter]: [
    Permission.SowAmendmentPropose,
    ...TERRITORY_MODULE,
    Permission.UserCreateSubProfile,
    Permission.AssistantUse,
  ],

  [Role.TerritoryReader]: [
    Permission.TerritorySwitch, Permission.NbdRulesView, Permission.QuoteRequestReceive,
    Permission.WoView, Permission.SrView, Permission.InvoiceView, Permission.VendorBillView,
    Permission.InspectionView, Permission.RsView, Permission.DocDownload,
    Permission.AssistantUse,
  ],

  [Role.SubProxy]: [
    Permission.WoUpdateStatus, Permission.WoComplete, Permission.WoChecklistComplete, Permission.WoView,
    Permission.BidView, Permission.BidSubmit,
    Permission.DocUpload, Permission.DocDownload, Permission.MsgReply,
  ],

  [Role.SubAdmin]: [...SUB_WORKER, Permission.UserCreateSubStaff, Permission.SubPayoutsConnectWrite],
  [Role.SubWriter]: SUB_WORKER,
  [Role.SubReader]: [
    Permission.WoView, Permission.BidView, Permission.VendorBillView,
    Permission.InspectionViewOwn, Permission.DocDownload,
  ],

  [Role.ClientWriter]: [
    // QuoteRequestCreate added 2026-08-14 (role-guard-parity gate): `POST /client/v1/quotes/requests`
    // names CLIENT_WRITER and the surface is shipped. The capability is unchanged by the 2026-08-20
    // nav merge — what moved is only WHERE the client reaches it: the "Request a quote" chip on the
    // single Requests screen, not a second tab (PORT_DEVIATIONS §134). Withholding it would be the
    // same shape as CorporateWriter holding no `Sr*` capability: the route admits them, the map did not.
    Permission.SrFile, Permission.QuoteRequestCreate, Permission.QuoteAccept, Permission.QuoteDecline,
    Permission.WoView, Permission.WoChecklistComplete, Permission.InvoiceView,
    Permission.SrView, Permission.DocDownload, Permission.MsgReply,
  ],
  [Role.ClientReader]: [
    Permission.WoView, Permission.InvoiceView, Permission.SrView, Permission.DocDownload,
  ],
};

/** Resolve a role string to its capability set (unknown role → empty). */
export function permissionsForRole(role: string): ReadonlySet<Permission> {
  const list = (ROLE_PERMISSIONS as Record<string, readonly Permission[]>)[role];
  return new Set(list ?? []);
}
